import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Clock, CheckCircle2, XCircle, Send } from "lucide-react";

const fmtDT = (s) => s ? new Date(s).toLocaleString("it-IT") : "—";
const STATO = {
  in_attesa: { l: "In attesa", c: "bg-amber-100 text-amber-800", i: Clock },
  approvata: { l: "Approvata", c: "bg-emerald-100 text-emerald-800", i: CheckCircle2 },
  rifiutata: { l: "Rifiutata", c: "bg-red-100 text-red-800", i: XCircle },
};

/** Iter122ck · SuperAdmin · Coda approvazioni richieste licenze condomìni. */
export default function SuperAdminRichiesteCondomini() {
  const [items, setItems] = useState([]);
  const [riep, setRiep] = useState(null);
  const [stato, setStato] = useState("in_attesa");

  const load = () => api.get(`/superadmin/licenze-condominio/richieste${stato ? `?stato=${stato}` : ""}`)
    .then((r) => { setItems(r.data.items || []); setRiep(r.data.riepilogo || null); })
    .catch(() => {});
  useEffect(() => { load(); }, [stato]);

  const approva = async (r) => {
    const nota = window.prompt(`Approvare la richiesta ${r.tipo} di ${r.tenant_nome || r.tenant_id}?\n\nNota per il tenant (opzionale):`, "");
    if (nota === null) return;
    try {
      const { data } = await api.post(`/superadmin/licenze-condominio/richieste/${r.id}/approva`, { nota });
      toast.success(`Approvata · created ${data.esiti.created} · updated ${data.esiti.updated} · errori ${data.esiti.errori.length}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const rifiuta = async (r) => {
    const nota = window.prompt(`Rifiutare la richiesta ${r.tipo} di ${r.tenant_nome || r.tenant_id}?\n\nMotivo (obbligatorio):`, "");
    if (nota === null || !nota.trim()) return;
    try {
      await api.post(`/superadmin/licenze-condominio/richieste/${r.id}/rifiuta`, { nota });
      toast.success("Richiesta rifiutata"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  return (
    <div className="space-y-3" data-testid="super-richieste-condomini">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg font-bold text-slate-800">Richieste licenze condomìni</h3>
        <select value={stato} onChange={(e) => setStato(e.target.value)}
                className="h-9 border-2 border-slate-200 rounded-md px-3 text-sm" data-testid="richieste-filter">
          <option value="in_attesa">In attesa {riep ? `(${riep.in_attesa})` : ""}</option>
          <option value="approvata">Approvate {riep ? `(${riep.approvate})` : ""}</option>
          <option value="rifiutata">Rifiutate {riep ? `(${riep.rifiutate})` : ""}</option>
          <option value="">Tutte</option>
        </select>
      </div>
      {items.length === 0 && (
        <Card className="p-4 text-center text-slate-400 text-sm">Nessuna richiesta.</Card>
      )}
      {items.map((r) => {
        const S = STATO[r.stato] || STATO.in_attesa; const Ic = S.i;
        return (
          <Card key={r.id} className="p-3 sm:p-4 border-2 space-y-2" data-testid={`richiesta-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full font-semibold text-xs inline-flex items-center gap-1 ${S.c}`}>
                <Ic className="w-3 h-3" /> {S.l}
              </span>
              <span className="font-semibold text-slate-800">{r.tipo.toUpperCase()}</span>
              <span className="text-slate-500">{r.tenant_nome || r.tenant_id} · {r.richiedente}</span>
              <span className="text-slate-400 text-xs ml-auto">{fmtDT(r.created_at)}</span>
            </div>
            <div className="text-xs text-slate-600">
              <b>Condomìni ({r.condominio_ids.length}):</b> {(r.condomini_snapshot || []).map((c) => c.nome).join(", ") || r.condominio_ids.join(", ")}
              {r.piano_snapshot && <div className="mt-0.5"><b>Piano:</b> {r.piano_snapshot.nome} · €{r.piano_snapshot.prezzo_annuo}/anno</div>}
              {r.attivazione_at && <div className="mt-0.5"><b>Attivazione:</b> {r.attivazione_at}</div>}
              {r.modalita_pagamento && <div className="mt-0.5"><b>Pagamento:</b> {r.modalita_pagamento}</div>}
              {r.motivo && <div className="mt-0.5 italic">Motivo: {r.motivo}</div>}
              {r.review_at && (
                <div className="mt-1 pt-1 border-t border-slate-200 text-slate-500">
                  Decisa {fmtDT(r.review_at)} da {r.review_by}{r.review_note && ` · "${r.review_note}"`}
                  {r.esiti && <span> · esiti: create {r.esiti.created}, updated {r.esiti.updated}, errori {r.esiti.errori.length}</span>}
                </div>
              )}
            </div>
            {r.stato === "in_attesa" && (
              <div className="flex items-center gap-2">
                <Button size="sm" className="h-9 bg-brand hover:bg-brand-dark" onClick={() => approva(r)}
                        data-testid={`richiesta-approva-${r.id}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Approva
                </Button>
                <Button size="sm" variant="outline" className="h-9 text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => rifiuta(r)} data-testid={`richiesta-rifiuta-${r.id}`}>
                  <XCircle className="w-4 h-4 mr-1" /> Rifiuta
                </Button>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
